/**
 * Query parameters that carry text a user typed (searches). They never reach analytics: the value becomes
 * `[redacted]` (review M9: the drawings as-built search is in the URL as `abQ`).
 */
export const FREE_TEXT_PARAMS = ['q', 'abQ', 'search', 'query'] as const;

/** A URL or path with every free-text query value replaced by `[redacted]`; anything unparseable comes back as is */
export function redactFreeText(url: string): string {
  if (!url || !url.includes('?')) return url;
  try {
    const absolute = /^[a-z][a-z0-9+.-]*:/i.test(url);
    const parsed = new URL(url, 'http://redact.local');
    let changed = false;
    for (const key of FREE_TEXT_PARAMS) {
      if (parsed.searchParams.has(key)) {
        parsed.searchParams.set(key, '[redacted]');
        changed = true;
      }
    }
    if (!changed) return url;
    return absolute ? parsed.toString() : `${parsed.pathname}${parsed.search}${parsed.hash}`;
  } catch {
    return url;
  }
}

/** The URL properties PostHog puts on events (and on the person, under $set / $set_once): redacted like the pageview */
export const URL_PROPERTIES = [
  '$current_url', '$pathname', '$referrer', '$initial_current_url', '$initial_pathname', '$initial_referrer', 'url', 'path',
] as const;

/**
 * Every event's URL properties through `redactFreeText` (PostHog `before_send`, via `redactCaptureResult`), so search
 * text in the address never leaves the browser on any event, not only `$pageview`.
 */
export function sanitizeAnalyticsProperties<T extends Record<string, unknown>>(properties: T): T {
  if (!properties || typeof properties !== 'object') return properties;
  const redact = (props: Record<string, unknown>): Record<string, unknown> => {
    const out = { ...props };
    for (const key of URL_PROPERTIES) {
      if (typeof out[key] === 'string') out[key] = redactFreeText(out[key] as string);
    }
    return out;
  };
  const out = redact(properties);
  for (const nested of ['$set', '$set_once']) {
    const value = out[nested];
    if (value && typeof value === 'object') out[nested] = redact(value as Record<string, unknown>);
  }
  return out as T;
}

/**
 * PostHog `before_send`: the event's properties and its person properties ($set / $set_once, top level on the captured
 * event) go through `sanitizeAnalyticsProperties`. A dropped event (null) stays dropped.
 */
export function redactCaptureResult<T extends { properties?: Record<string, unknown>; $set?: Record<string, unknown>; $set_once?: Record<string, unknown> }>(
  event: T | null,
): T | null {
  if (!event) return event;
  return {
    ...event,
    ...(event.properties ? { properties: sanitizeAnalyticsProperties(event.properties) } : {}),
    ...(event.$set ? { $set: sanitizeAnalyticsProperties(event.$set) } : {}),
    ...(event.$set_once ? { $set_once: sanitizeAnalyticsProperties(event.$set_once) } : {}),
  };
}
